import type { TreeNode } from '../types';

import { getParentCheckboxState } from './getParentCheckboxState';
import { traverseTreeChildren } from './traverseTreeChildren';

/** Аргументы функции updateTreeNodeChildrenSelectedState */
interface UpdateTreeNodeChildrenModifiedStateArgs {
    /** Узел дерева, выбранное состояние дочерних узлов которого должно быть обновлено */
    item: TreeNode;

    /** Новое выбранное состояние, которое должно быть применено к включенным дочерним узлам */
    newState: boolean;

    /**
     * Мапа, содержащая информацию о состоянии узлов до изменения состояния
     */
    prevSelectedState: Map<string, boolean>;

    /**
     * Мапа, содержащая информацию о состоянии узлов при изменении
     * Мутируется внутри функции
     **/
    selectedState: Map<string, boolean>;
}

/**
 * Обновляет выбранное состояние всех дочерних элементов узла дерева на основе предоставленного нового значения,
 * учитывая задизейбленное состояние узлов и их ближайших задизейбленных родителей.
 *
 * Для каждого дочернего элемента, если дочерний элемент задизейблен, переключает или удаляет его выбранное состояние
 * на основе newState и существующего состояния. Если дочерний элемент не задизейблен и имеет задизейбленного родителя,
 * он устанавливает выбранное состояние дочернего элемента на newState; в противном случае он удаляет выбранное
 * состояние дочернего элемента.
 *
 * @param args Аргументы функции.
 * @kind helper
 */
export function updateTreeNodeChildrenSelectedState({
    item,
    prevSelectedState,
    newState,
    selectedState,
}: UpdateTreeNodeChildrenModifiedStateArgs): void {
    if (!item.children) {
        return;
    }

    /**
     * Отображаемое состояние узлов дерева. Это то состояние, которе отображается до того, как было
     * обновлено состояние всех узлов потомков кликнутой ноды.
     **/
    const nodesDisplayState = new Map<string, boolean>();

    /**
     * Вычисляем отображаемое состояние узлов-потомков. Вычисляем только состояние потомков, а не всего
     * дерева поскольку обновлять подкапотное состояние будем только для потомков.
     */
    traverseTreeChildren([item], node => {
        /** Получаем отображаемое состояние родительской ноды (если она есть) */
        const parentDisplayState = node.parent ? nodesDisplayState.get(node.parent.id) : undefined;
        /**
         * Определяем отображаемое текущей ноды.
         * Если в мапе предыдущих состояний есть состояние для текущей ноды, то берем его,
         * иначе берем отображаемое состояние родительской ноды, поскольку оно наследуется.
         */
        const nodeDisplayState = prevSelectedState.get(node.id) ?? parentDisplayState;

        /**
         * Сохраняем отображаемое состояние текущего узла. Если его не удалось
         * вычислить на основе предыдущего состояния или отображаемого состояния родительского узла,
         * то устанавливаем состояние, противоположное newState, поскольку newState - это новое состояние,
         * а его противоположное значение - состояние до начала обновления дерева.
         */
        nodesDisplayState.set(node.id, nodeDisplayState ?? !newState);
    });

    traverseTreeChildren(item.children, child => {
        /**
         * Вычисляем актуальное отображаемое состояние ноды-родителя на основе
         * нового состояния и обновляемой мапы состояний
         **/
        const parentDisplayState = getParentCheckboxState({
            item: child,
            checkboxMap: selectedState,
            initialCheckboxValue: newState,
        });

        /**
         * Если узел задизейблен, то отображаемое состояние дочернего узла должно быть
         * противоположное состоянию родительского узла. Если состояния совпадают, то удаляем
         * состояние дочернего узла, иначе устанавливаем состояние дочернего узла на противоположное
         * состояние родительского узла.
         */
        if (child.isDisabled) {
            const childDisplayState = nodesDisplayState.get(child.id);

            /**
             * Если состояние дочернего узла и состояние родительского узла совпадают, то удаляем
             * состояние дочернего узла, поскольку дочерний узел в этом случае должен наследовать
             * состояние родительского узла.
             */
            if (parentDisplayState === childDisplayState) {
                selectedState.delete(child.id);
            } else {
                /**
                 * Иначе если состояние дочернего узла и состояние родительского узла не совпадают,
                 * то сохраняем дочернему узлу его состояние, равное отображаемому состоянию, которое
                 * было до того, как начали обновлять состояние дерева.
                 */
                selectedState.set(child.id, Boolean(childDisplayState));
            }
            /**
             * Если потомок не задизейблен и отображаемое состояние его родителя совпадает с устанавливаемым,
             * то удаляем состояние потомка чтобы он наследовал отображаемое состояние от родителя
             **/
        } else if (parentDisplayState === newState) {
            selectedState.delete(child.id);
            /**
             * Если потомок не задизейблен и отображаемое состояние его родителя не совпадает с устанавливаемым,
             * то устанавливаем состояние потомка на противоположное состоянию родителя. Такое может быть,
             * например, если родительский узел задизейблен.
             */
        } else {
            selectedState.set(child.id, newState);
        }
    });
}
